/** The 3px bar that says how much of something there is.
 *
 * Coverage per side on a track card, free tier consumed on the costs table,
 * storage by data type on the dashboard — all the same shape: an accent fill
 * on a neutral-900 bed, thin enough to sit inside a line of 11px text without
 * becoming the subject.
 *
 * The number is always written beside the bar in the design, so by default
 * the bar itself is hidden from assistive technology rather than repeating
 * it. Pass `label` where the bar stands alone, and it becomes a progressbar
 * that states its own value.
 */

import type { CSSProperties } from 'react';

export interface BarProps {
  /** Where the fill reaches, in the same units as `max`. */
  value: number;
  max?: number;
  height?: number;
  /** `warn` for a free tier nearly spent, `neutral` for context. */
  tone?: 'accent' | 'warn' | 'neutral';
  /** Makes the bar its own progressbar with this accessible name. */
  label?: string;
  className?: string;
  style?: CSSProperties;
}

const FILL: Record<NonNullable<BarProps['tone']>, string> = {
  accent: 'var(--color-accent-600)',
  warn: 'var(--color-warn)',
  neutral: 'var(--color-neutral-600)',
};

export function Bar({ value, max = 100, height = 3, tone = 'accent', label, className, style }: BarProps) {
  const safeMax = max > 0 ? max : 100;
  const clamped = Math.max(0, Math.min(safeMax, Number.isFinite(value) ? value : 0));
  const pct = (clamped / safeMax) * 100;

  return (
    <div
      className={className}
      role={label ? 'progressbar' : undefined}
      aria-label={label}
      aria-valuenow={label ? Math.round(clamped) : undefined}
      aria-valuemin={label ? 0 : undefined}
      aria-valuemax={label ? safeMax : undefined}
      aria-hidden={label ? undefined : true}
      style={{
        height,
        borderRadius: Math.max(2, height / 2),
        background: 'var(--color-neutral-900)',
        overflow: 'hidden',
        minWidth: 0,
        ...style,
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${pct}%`,
          borderRadius: Math.max(2, height / 2),
          background: FILL[tone],
        }}
      />
    </div>
  );
}

export default Bar;
